import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';

const SUPER_ADMIN = 'chandlercharlescrane@gmail.com';

const AdminConfig = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [currentUserEmail, setCurrentUserEmail] = useState('');

  const [adminEmails, setAdminEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [missionText, setMissionText] = useState('');
  const [scheduleText, setScheduleText] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [prayerRequestsSheetUrl, setPrayerRequestsSheetUrl] = useState('');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login', { state: { from: location.pathname } });
        return;
      }
      if (config.configLoaded && config.adminEmails.length > 0 && !config.adminEmails.includes(user.email)) {
        navigate('/');
        return;
      }
      if (config.configLoaded) {
        setCurrentUserEmail(user.email);
        await loadConfig();
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [config.configLoaded, config.adminEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfig = async () => {
    const snap = await getDoc(doc(db, 'config', 'app'));
    const data = snap.exists() ? snap.data() : {};
    setAdminEmails(data.adminEmails ?? config.adminEmails ?? []);
    setMissionText(data.missionText ?? config.missionText ?? '');
    setScheduleText(data.scheduleText ?? config.scheduleText ?? '');
    setContactEmail(data.contactEmail ?? config.contactEmail ?? '');
    setPrayerRequestsSheetUrl(data.prayerRequestsSheetUrl ?? config.prayerRequestsSheetUrl ?? '');
  };

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || adminEmails.includes(email)) return;
    setAdminEmails([...adminEmails, email]);
    setNewEmail('');
    setShowAddEmail(false);
  };

  const removeEmail = (email) => {
    setAdminEmails(adminEmails.filter(e => e !== email));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await setDoc(doc(db, 'config', 'app'), {
        adminEmails,
        missionText: missionText.trim(),
        scheduleText: scheduleText.trim(),
        contactEmail: contactEmail.trim(),
        prayerRequestsSheetUrl: prayerRequestsSheetUrl.trim()
      });
      await config.refreshConfig();
      setSaveMsg('Saved!');
    } catch (err) {
      console.error(err);
      setSaveMsg('Error saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ minHeight: '100vh' }}><LoadingAnimation /></div>;

  const canManageEmails = currentUserEmail === SUPER_ADMIN;

  return (
    <div className="stt-admin-page">
      <a href="/admin" className="stt-admin-back">← Back to Admin</a>
      <h1 className="stt-admin-title">Site Config</h1>
      <p style={{ color: '#666', fontSize: '0.88rem' }}>Changes here take effect immediately — no code deploy needed.</p>
      <hr />

      <div className="stt-admin-card" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        <div>
          <label className="stt-form-label"><strong>Mission Text</strong></label>
          <p className="stt-form-hint">Displayed in the "Our Mission" section on the homepage.</p>
          <textarea
            className="form-control"
            rows={5}
            value={missionText}
            onChange={e => setMissionText(e.target.value)}
            placeholder="Enter your community mission statement here..."
          />
        </div>

        <div>
          <label className="stt-form-label"><strong>Faith &amp; Fellowship Schedule Text</strong></label>
          <p className="stt-form-hint">Displayed in the weekly schedule section. Use line breaks for formatting.</p>
          <textarea
            className="form-control"
            rows={6}
            value={scheduleText}
            onChange={e => setScheduleText(e.target.value)}
            placeholder="e.g. We invite you to join us Wednesday evenings..."
          />
        </div>

        <div>
          <label className="stt-form-label"><strong>Contact Email</strong></label>
          <p className="stt-form-hint">Displayed in the Contact section on the homepage.</p>
          <input
            type="email"
            className="form-control"
            value={contactEmail}
            onChange={e => setContactEmail(e.target.value)}
          />
        </div>

        <div>
          <label className="stt-form-label"><strong>Google Sheets URL (Prayer Requests)</strong></label>
          <p className="stt-form-hint">The Google Apps Script Web App URL that receives prayer request submissions.</p>
          <details style={{ marginBottom: '8px' }}>
            <summary style={{ fontSize: '0.8rem', color: 'var(--navy)', cursor: 'pointer', fontWeight: 500 }}>Setup instructions</summary>
            <ol style={{ fontSize: '0.8rem', color: 'var(--color-secondary)', paddingLeft: '20px', marginTop: '8px' }}>
              <li>Create a Google Sheet named <em>Southie Totus Tuus — Prayer Requests</em> with row 1 headers: <strong>Timestamp | Name | Message</strong></li>
              <li>In the Sheet, go to <strong>Extensions → Apps Script</strong>, paste the script below, and save it as <code>PrayerRequests</code>:
                <pre style={{ background: '#f5f5f5', padding: '10px', fontSize: '0.75rem', marginTop: '6px', overflowX: 'auto', border: '1px solid var(--divider)' }}>{`function doPost(e) {
  var sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
  var data = JSON.parse(e.postData.contents);
  sheet.appendRow([new Date().toLocaleString(), data.name || "Anonymous", data.message]);
  return ContentService.createTextOutput("OK");
}`}</pre>
              </li>
              <li>Click <strong>Deploy → New deployment</strong>: Type = <em>Web app</em>, Execute as = <em>Me</em>, Who has access = <em>Anyone</em></li>
              <li>Click <strong>Deploy</strong>, authorize when prompted, then copy the Web App URL and paste it in the field below</li>
            </ol>
          </details>
          <input
            type="url"
            className="form-control"
            value={prayerRequestsSheetUrl}
            onChange={e => setPrayerRequestsSheetUrl(e.target.value)}
            placeholder="https://script.google.com/macros/s/.../exec"
          />
        </div>

        <div>
          <label className="stt-form-label"><strong>Admin Emails</strong></label>
          <p className="stt-form-hint">
            These accounts can access all admin pages.
            {canManageEmails
              ? ' Click × to remove an email, or + Add email to add one.'
              : ' Only the super-admin can add or remove emails.'}
          </p>
          <div style={{ border: '1px solid var(--divider)', borderRadius: '2px' }}>
            {adminEmails.map((email, i) => (
              <div
                key={email}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 14px',
                  borderBottom: i < adminEmails.length - 1 || canManageEmails ? '1px solid var(--divider)' : 'none',
                  background: 'white'
                }}
              >
                <span style={{ flex: 1, fontSize: '0.9rem', fontFamily: 'monospace' }}>{email}</span>
                {canManageEmails && email !== currentUserEmail && (
                  <button
                    onClick={() => removeEmail(email)}
                    title="Remove"
                    style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '1.1rem', padding: '0 4px', lineHeight: 1 }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}

            {canManageEmails && (
              <div style={{ padding: '10px 14px', background: 'var(--background-warm)' }}>
                {showAddEmail ? (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      placeholder="new@email.com"
                      value={newEmail}
                      onChange={e => setNewEmail(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') addEmail();
                        if (e.key === 'Escape') { setShowAddEmail(false); setNewEmail(''); }
                      }}
                      autoFocus
                      style={{ maxWidth: '260px' }}
                    />
                    <button className="btn btn-sm btn-outline-secondary" onClick={addEmail}>Add</button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => { setShowAddEmail(false); setNewEmail(''); }}>Cancel</button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowAddEmail(true)}
                    style={{ background: 'none', border: 'none', color: 'var(--navy)', cursor: 'pointer', fontSize: '0.85rem', padding: 0, fontFamily: 'var(--font-body)' }}
                  >
                    + Add email
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn stt-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Config'}
          </button>
          {saveMsg && (
            <span className={`stt-save-message ${saveMsg === 'Saved!' ? 'success' : 'error'}`}>
              {saveMsg}
            </span>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminConfig;
